import { useState, useEffect } from "react";
import { getPhotoUrl } from '@/utils/photoUrl';
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { User, Mail, Lock, LogIn, UserCircle, Eye, EyeOff, Shield, Users, Search, UserPlus, Phone, Calendar, ArrowLeft, CheckCircle, Camera, Upload, Loader2 } from "lucide-react";
import { useLanguage, LanguageSelectorCompact } from "@/i18n";

// Função para máscara de telefone dos EUA (XXX) XXX-XXXX
const formatUSPhone = (value) => {
  if (!value) return "";
  // Remove tudo que não é número
  const numbers = value.replace(/\D/g, "");
  // Aplica a máscara
  if (numbers.length <= 3) {
    return `(${numbers}`;
  } else if (numbers.length <= 6) {
    return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
  } else if (numbers.length <= 10) {
    return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6)}`;
  }
  return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
};

// Posições disponíveis
const POSITIONS = ["Ponteiro", "Oposto", "Central", "Levantador", "Líbero"];

// Player Registration Component
function PlayerRegister({ onBack }) {
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    position: "",
    email: "",
    phone: "",
    birth_date: "",
    password: "",
    confirmPassword: "",
    photo_url: ""
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handlePhoneChange = (e) => {
    const formatted = formatUSPhone(e.target.value);
    setFormData(prev => ({ ...prev, phone: formatted }));
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error("Use apenas JPG, PNG, GIF ou WEBP");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Máximo 10MB");
      return;
    }
    
    setIsUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      const res = await axios.post(`${API}/upload/photo/cloudinary`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData(prev => ({ ...prev, photo_url: res.data.photo_url }));
      toast.success("Foto enviada!");
    } catch (error) {
      toast.error("Erro no upload da foto");
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validações
    if (!formData.first_name.trim()) {
      toast.error("Digite seu primeiro nome");
      return;
    }
    if (!formData.last_name.trim()) {
      toast.error("Digite seu sobrenome");
      return;
    }
    if (!formData.position) {
      toast.error("Selecione sua posição");
      return;
    }
    if (!formData.email.trim()) {
      toast.error("Digite seu email");
      return;
    }
    if (formData.password.length < 4) {
      toast.error("A senha deve ter pelo menos 4 caracteres");
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      toast.error("As senhas não coincidem");
      return;
    }

    setIsLoading(true);
    try {
      await axios.post(`${API}/players/register`, {
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        position: formData.position,
        email: formData.email.trim(),
        phone: formData.phone || null,
        birth_date: formData.birth_date || null,
        password: formData.password,
        photo_url: formData.photo_url || null
      });
      
      setSuccess(true);
      toast.success("Cadastro realizado com sucesso!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao realizar cadastro");
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className="space-y-6 animate-fade-in text-center">
        <CheckCircle className="h-16 w-16 mx-auto text-green-500" />
        <div>
          <h3 className="text-xl font-bold text-white mb-2">Cadastro Realizado!</h3>
          <p className="text-slate-400 text-sm">
            Seu cadastro foi enviado para aprovação. Você receberá um email quando for aprovado.
          </p>
        </div>
        <Button
          onClick={onBack}
          className="w-full h-12 bg-slate-800 hover:bg-slate-700 text-white rounded-xl"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar ao Login
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-5 animate-fade-in">
      <div className="text-center">
        <UserPlus className="h-10 w-10 mx-auto text-green-500 mb-2" />
        <p className="text-slate-400 text-xs">
          Preencha seus dados para solicitar acesso
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Foto do Jogador */}
        <div className="flex flex-col items-center gap-3">
          <div className="relative">
            <div className="w-24 h-24 rounded-full bg-slate-800 flex items-center justify-center overflow-hidden border-2 border-slate-700">
              {formData.photo_url ? (
                <img src={formData.photo_url} alt="Foto" className="w-full h-full object-cover" />
              ) : (
                <Camera className="h-8 w-8 text-slate-500" />
              )}
            </div>
            {formData.photo_url && (
              <button 
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, photo_url: "" }))}
                className="absolute -top-1 -right-1 bg-red-500 rounded-full p-1 text-white hover:bg-red-600"
              >
                ✕
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <label className="cursor-pointer">
              <div className={`flex items-center gap-2 px-4 py-2 rounded-lg border border-dashed transition-colors text-sm ${isUploading ? 'bg-green-500/10 border-green-500' : 'border-slate-600 hover:border-green-500'}`}>
                {isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                <span>{isUploading ? "Enviando..." : "Galeria"}</span>
              </div>
              <input 
                type="file" 
                accept="image/jpeg,image/png,image/gif,image/webp,image/*" 
                className="hidden" 
                onChange={handlePhotoUpload} 
                disabled={isUploading} 
              />
            </label>
            <label className="cursor-pointer">
              <div className={`flex items-center gap-2 px-4 py-2 rounded-lg border border-dashed transition-colors text-sm ${isUploading ? 'bg-green-500/10 border-green-500' : 'border-slate-600 hover:border-green-500'}`}>
                {isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
                <span>{isUploading ? "Enviando..." : "Câmera"}</span>
              </div>
              <input 
                type="file" 
                accept="image/*" 
                capture="environment"
                className="hidden" 
                onChange={handlePhotoUpload} 
                disabled={isUploading} 
              />
            </label>
          </div>
          <p className="text-xs text-slate-500">Adicione sua foto (opcional)</p>
        </div>

        {/* Nome e Sobrenome */}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs font-medium uppercase tracking-wider text-slate-400">
              Primeiro Nome *
            </Label>
            <Input
              type="text"
              placeholder="João"
              value={formData.first_name}
              onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
              className="h-12 bg-slate-900/50 border-slate-700/50 rounded-xl text-white placeholder:text-slate-500 focus:border-green-500/50"
              required
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-medium uppercase tracking-wider text-slate-400">
              Sobrenome *
            </Label>
            <Input
              type="text"
              placeholder="Silva"
              value={formData.last_name}
              onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
              className="h-12 bg-slate-900/50 border-slate-700/50 rounded-xl text-white placeholder:text-slate-500 focus:border-green-500/50"
              required
            />
          </div>
        </div>

        {/* Posição */}
        <div className="space-y-1">
          <Label className="text-xs font-medium uppercase tracking-wider text-slate-400">
            Posição *
          </Label>
          <Select value={formData.position} onValueChange={(val) => setFormData(prev => ({ ...prev, position: val }))}>
            <SelectTrigger className="h-12 bg-slate-900/50 border-slate-700/50 rounded-xl text-white focus:border-green-500/50">
              <SelectValue placeholder="Selecione sua posição" />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700">
              {POSITIONS.map(pos => (
                <SelectItem key={pos} value={pos} className="text-white hover:bg-slate-800">
                  {pos}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Email */}
        <div className="space-y-1">
          <Label className="text-xs font-medium uppercase tracking-wider text-slate-400">
            Email *
          </Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <Input
              type="email"
              placeholder="seu@email.com"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              className="pl-10 h-12 bg-slate-900/50 border-slate-700/50 rounded-xl text-white placeholder:text-slate-500 focus:border-green-500/50"
              required
            />
          </div>
        </div>

        {/* Telefone e Data de Nascimento */}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs font-medium uppercase tracking-wider text-slate-400">
              Telefone
            </Label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
              <Input
                type="tel"
                placeholder="(555) 123-4567"
                value={formData.phone}
                onChange={handlePhoneChange}
                maxLength={14}
                className="pl-10 h-12 bg-slate-900/50 border-slate-700/50 rounded-xl text-white placeholder:text-slate-500 focus:border-green-500/50"
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-medium uppercase tracking-wider text-slate-400">
              Data Nasc.
            </Label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
              <Input
                type="date"
                value={formData.birth_date}
                onChange={(e) => setFormData(prev => ({ ...prev, birth_date: e.target.value }))}
                className="pl-10 h-12 bg-slate-900/50 border-slate-700/50 rounded-xl text-white focus:border-green-500/50"
              />
            </div>
          </div>
        </div>

        {/* Senha */}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs font-medium uppercase tracking-wider text-slate-400">
              Senha *
            </Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
              <Input
                type={showPassword ? "text" : "password"}
                placeholder="••••"
                value={formData.password}
                onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                className="pl-10 h-12 bg-slate-900/50 border-slate-700/50 rounded-xl text-white placeholder:text-slate-500 focus:border-green-500/50"
                required
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-medium uppercase tracking-wider text-slate-400">
              Confirmar *
            </Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
              <Input
                type={showPassword ? "text" : "password"}
                placeholder="••••"
                value={formData.confirmPassword}
                onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                className="pl-10 pr-10 h-12 bg-slate-900/50 border-slate-700/50 rounded-xl text-white placeholder:text-slate-500 focus:border-green-500/50"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
        </div>

        <div className="flex gap-3 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={onBack}
            className="flex-1 h-12 border-slate-700 text-slate-300 rounded-xl"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar
          </Button>
          <Button
            type="submit"
            disabled={isLoading}
            className="flex-1 h-12 bg-green-600 hover:bg-green-700 text-white font-bold uppercase tracking-wider rounded-xl shadow-[0_0_20px_rgba(34,197,94,0.3)] disabled:opacity-50"
          >
            {isLoading ? (
              <div className="flex items-center gap-2">
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                Enviando...
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <UserPlus className="h-4 w-4" />
                Cadastrar
              </div>
            )}
          </Button>
        </div>

        <p className="text-xs text-center text-slate-500 pt-2">
          * Campos obrigatórios. Seu cadastro será analisado pelo administrador.
        </p>
      </form>
    </div>
  );
}

// Simple Player Login Component - Select from list
function SimplePlayerLogin({ isLoading, setIsLoading, setEvaluator }) {
  const { t } = useLanguage();
  const [players, setPlayers] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [loadingPlayers, setLoadingPlayers] = useState(true);

  useEffect(() => {
    const fetchPlayers = async () => {
      try {
        const response = await axios.get(`${API}/players`);
        // Sort alphabetically
        const sorted = response.data.sort((a, b) => {
          const nameA = getPlayerName(a).toLowerCase();
          const nameB = getPlayerName(b).toLowerCase();
          return nameA.localeCompare(nameB, 'pt-BR');
        });
        setPlayers(sorted);
      } catch (error) {
        toast.error(t('common.error'));
      } finally {
        setLoadingPlayers(false);
      }
    };
    fetchPlayers();
  }, [t]);

  const getPlayerName = (p) => p.first_name ? `${p.first_name} ${p.last_name}`.trim() : p.name;

  const filteredPlayers = players.filter(p => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    const name = getPlayerName(p).toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    return name.includes(query);
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!selectedPlayer) {
      toast.error(t('login.selectPlayer') || "Selecione seu nome");
      return;
    }
    if (!password) {
      toast.error(t('login.enterPassword') || "Digite a senha");
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/players/simple-login`, {
        player_id: selectedPlayer,
        password: password
      });
      setEvaluator({ ...response.data, isPlayer: true, isSimplePlayer: true });
      const playerName = `${response.data.first_name || ''} ${response.data.last_name || ''}`.trim() || 'Jogador';
      toast.success(`${t('dashboard.welcome')}, ${playerName}!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('login.invalidCredentials'));
    } finally {
      setIsLoading(false);
    }
  };

  const selectedPlayerObj = players.find(p => p.id === selectedPlayer);

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="text-center">
        <Users className="h-12 w-12 mx-auto text-[#CCFF00] mb-3" />
        <p className="text-slate-400 text-sm">
          {t('login.findYourName') || 'Encontre seu nome na lista e entre com a senha'}
        </p>
      </div>

      <form onSubmit={handleLogin} className="space-y-5">
        {/* Search */}
        <div className="space-y-2">
          <Label className="text-xs font-medium uppercase tracking-wider text-slate-400">
            {t('login.searchYourName') || 'Buscar seu nome'}
          </Label>
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500" />
            <Input
              type="text"
              placeholder={t('login.typeYourName') || 'Digite seu nome...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 h-14 bg-slate-900/50 border-slate-700/50 rounded-xl text-white placeholder:text-slate-500 focus:border-[#CCFF00]/50 focus:ring-[#CCFF00]/20"
            />
          </div>
        </div>

        {/* Player Select */}
        <div className="space-y-2">
          <Label className="text-xs font-medium uppercase tracking-wider text-slate-400">
            {t('login.selectYourName') || 'Selecione seu nome'}
          </Label>
          {loadingPlayers ? (
            <div className="h-14 bg-slate-900/50 rounded-xl flex items-center justify-center">
              <div className="animate-spin h-5 w-5 border-2 border-[#CCFF00] border-t-transparent rounded-full" />
            </div>
          ) : (
            <Select value={selectedPlayer} onValueChange={setSelectedPlayer}>
              <SelectTrigger className="h-14 bg-slate-900/50 border-slate-700/50 rounded-xl text-white focus:border-[#CCFF00]/50">
                <SelectValue placeholder={t('login.chooseYourName') || 'Escolha seu nome...'} />
              </SelectTrigger>
              <SelectContent className="max-h-60 bg-slate-900 border-slate-700">
                {filteredPlayers.length === 0 ? (
                  <div className="py-4 text-center text-sm text-slate-400">
                    {searchQuery ? t('players.noPlayers') : t('common.noData')}
                  </div>
                ) : (
                  filteredPlayers.map((player) => (
                    <SelectItem key={player.id} value={player.id} className="text-white hover:bg-slate-800">
                      <div className="flex items-center gap-2">
                        <span>{getPlayerName(player)}</span>
                        <span className="text-xs text-slate-400">({player.position})</span>
                      </div>
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          )}
          {filteredPlayers.length > 0 && (
            <p className="text-xs text-slate-500">
              {filteredPlayers.length} {t('players.playersFound') || 'jogador(es)'}
            </p>
          )}
        </div>

        {/* Show selected player photo */}
        {selectedPlayerObj && selectedPlayerObj.photo_url && (
          <div className="flex justify-center">
            <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-[#CCFF00]/50 bg-slate-800">
              <img 
                src={getPhotoUrl(selectedPlayerObj.photo_url)}
                alt={getPlayerName(selectedPlayerObj)}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        )}

        {/* Password */}
        <div className="space-y-2">
          <Label className="text-xs font-medium uppercase tracking-wider text-slate-400">
            {t('login.password')}
          </Label>
          <div className="relative">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500" />
            <Input
              type={showPassword ? "text" : "password"}
              placeholder={t('login.enterPassword') || 'Digite a senha'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="pl-12 pr-12 h-14 bg-slate-900/50 border-slate-700/50 rounded-xl text-white placeholder:text-slate-500 focus:border-[#CCFF00]/50 focus:ring-[#CCFF00]/20"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"
            >
              {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
        </div>

        <Button
          type="submit"
          disabled={isLoading || !selectedPlayer}
          className="w-full h-14 bg-[#CCFF00] hover:bg-[#CCFF00]/90 text-black font-bold uppercase tracking-wider rounded-xl shadow-[0_0_30px_rgba(204,255,0,0.3)] disabled:opacity-50"
        >
          {isLoading ? (
            <div className="flex items-center gap-2">
              <div className="animate-spin h-5 w-5 border-2 border-black border-t-transparent rounded-full" />
              {t('login.entering')}
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <LogIn className="h-5 w-5" />
              {t('login.enterButton')}
            </div>
          )}
        </Button>
      </form>
    </div>
  );
}

// Evaluator Login Component
function EvaluatorLoginTab({ isLoading, setIsLoading, setEvaluator }) {
  const { t } = useLanguage();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/evaluators/login`, { email, password });
      // A resposta tem a estrutura: {token, evaluator: {...}}
      const evaluatorData = response.data.evaluator || response.data;
      setEvaluator(evaluatorData);
      toast.success(t('common.success') + "!");
    } catch (error) {
      toast.error(error.response?.data?.detail || t('login.invalidCredentials'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="text-center">
        <Shield className="h-12 w-12 mx-auto text-[#007AFF] mb-3" />
        <p className="text-slate-400 text-sm">
          {t('login.evaluatorAccess') || 'Acesso para técnicos, avaliadores e administradores'}
        </p>
      </div>

      <form onSubmit={handleLogin} className="space-y-5">
        <div className="space-y-2">
          <Label className="text-xs font-medium uppercase tracking-wider text-slate-400">
            {t('login.email')}
          </Label>
          <div className="relative">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500" />
            <Input
              type="email"
              placeholder="seu@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="pl-12 h-14 bg-slate-900/50 border-slate-700/50 rounded-xl text-white placeholder:text-slate-500 focus:border-[#007AFF]/50 focus:ring-[#007AFF]/20"
              required
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-xs font-medium uppercase tracking-wider text-slate-400">
            {t('login.password')}
          </Label>
          <div className="relative">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500" />
            <Input
              type={showPassword ? "text" : "password"}
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="pl-12 pr-12 h-14 bg-slate-900/50 border-slate-700/50 rounded-xl text-white placeholder:text-slate-500 focus:border-[#007AFF]/50 focus:ring-[#007AFF]/20"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"
            >
              {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
        </div>

        <Button
          type="submit"
          disabled={isLoading}
          className="w-full h-14 bg-[#007AFF] hover:bg-[#007AFF]/90 text-white font-bold uppercase tracking-wider rounded-xl shadow-[0_0_30px_rgba(0,122,255,0.3)] disabled:opacity-50"
        >
          {isLoading ? (
            <div className="flex items-center gap-2">
              <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
              {t('login.entering')}
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <LogIn className="h-5 w-5" />
              {t('login.enterButton')}
            </div>
          )}
        </Button>
      </form>
    </div>
  );
}

export default function LoginPage({ setEvaluator }) {
  const { t } = useLanguage();
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("player"); // "player", "evaluator", or "register"
  const [logoClickCount, setLogoClickCount] = useState(0);
  const [showSecretLogin, setShowSecretLogin] = useState(false);
  const [secretPassword, setSecretPassword] = useState("");
  const [lastClickTime, setLastClickTime] = useState(0);

  // Handle logo click for secret admin login
  const handleLogoClick = () => {
    const now = Date.now();
    // Reset count if more than 2 seconds between clicks
    if (now - lastClickTime > 2000) {
      setLogoClickCount(1);
    } else {
      const newCount = logoClickCount + 1;
      setLogoClickCount(newCount);
      if (newCount >= 5) {
        setShowSecretLogin(true);
        setLogoClickCount(0);
      }
    }
    setLastClickTime(now);
  };

  const handleSecretLogin = async (e) => {
    e.preventDefault();
    if (!secretPassword) {
      toast.error(t('login.invalidCredentials'));
      return;
    }
    
    setIsLoading(true);
    try {
      // Validate password against backend API (secure validation)
      const response = await axios.post(`${API}/evaluators/admin-login`, {
        password: secretPassword
      });
      
      if (response.data.token) {
        setEvaluator(response.data.evaluator);
        toast.success("Acesso de administrador concedido!");
        setShowSecretLogin(false);
        setSecretPassword("");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao acessar como admin");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4 relative overflow-hidden">
      {/* Language Selector */}
      <div className="absolute top-4 right-4 z-50">
        <LanguageSelectorCompact />
      </div>

      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-1/2 -left-1/2 w-full h-full bg-gradient-to-br from-[#007AFF]/10 to-transparent rounded-full blur-3xl" />
        <div className="absolute -bottom-1/2 -right-1/2 w-full h-full bg-gradient-to-tl from-[#CCFF00]/10 to-transparent rounded-full blur-3xl" />
      </div>

      <div className="w-full max-w-md relative z-10">
        {/* Logo - clickable for secret admin */}
        <div className="text-center mb-8">
          <img 
            src="/logo.png" 
            alt="VolleyxBrasil" 
            className="w-24 h-24 mx-auto mb-4 cursor-pointer select-none" 
            onClick={handleLogoClick}
            draggable="false"
          />
          <h1 className="text-3xl font-black text-white tracking-tight" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            VOLLEYX BRASIL
          </h1>
          <p className="text-slate-400 text-sm mt-1">{t('login.subtitle')}</p>
        </div>

        {/* Secret Admin Login Dialog */}
        {showSecretLogin && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6 max-w-sm w-full shadow-2xl">
              <div className="text-center mb-6">
                <Shield className="h-12 w-12 mx-auto text-red-500 mb-3" />
                <h2 className="text-xl font-bold text-white">Acesso Restrito</h2>
                <p className="text-slate-400 text-sm">Digite a senha de administrador</p>
              </div>
              <form onSubmit={handleSecretLogin} className="space-y-4">
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500" />
                  <Input
                    type="password"
                    placeholder="Senha do Admin"
                    value={secretPassword}
                    onChange={(e) => setSecretPassword(e.target.value)}
                    className="pl-12 h-14 bg-slate-800/50 border-slate-700/50 rounded-xl text-white"
                    autoFocus
                  />
                </div>
                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowSecretLogin(false);
                      setSecretPassword("");
                    }}
                    className="flex-1 h-12 border-slate-700 text-slate-300"
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="flex-1 h-12 bg-red-600 hover:bg-red-700 text-white"
                  >
                    {isLoading ? t('login.entering') : t('login.enterButton')}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Login Card */}
        <div className="bg-slate-900/50 backdrop-blur-xl border border-slate-800/50 rounded-2xl p-6 shadow-2xl">
          {/* Tab Buttons - Only show when not in register mode */}
          {activeTab !== "register" && (
            <>
              <div className="flex gap-2 mb-4">
                <button
                  onClick={() => setActiveTab("player")}
                  className={`flex-1 py-3 px-4 rounded-xl font-bold uppercase text-sm tracking-wider transition-all ${
                    activeTab === "player"
                      ? "bg-[#CCFF00] text-black shadow-[0_0_20px_rgba(204,255,0,0.3)]"
                      : "bg-slate-800/50 text-slate-400 hover:bg-slate-800"
                  }`}
                >
                  <div className="flex items-center justify-center gap-2">
                    <Users className="h-4 w-4" />
                    {t('login.playerTab')}
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab("evaluator")}
                  className={`flex-1 py-3 px-4 rounded-xl font-bold uppercase text-sm tracking-wider transition-all ${
                    activeTab === "evaluator"
                      ? "bg-[#007AFF] text-white shadow-[0_0_20px_rgba(0,122,255,0.3)]"
                      : "bg-slate-800/50 text-slate-400 hover:bg-slate-800"
                  }`}
                >
                  <div className="flex items-center justify-center gap-2">
                    <Shield className="h-4 w-4" />
                    {t('login.evaluatorTab')}
                  </div>
                </button>
              </div>
              
              {/* Register Button - Below tabs */}
              {activeTab === "player" && (
                <button
                  onClick={() => setActiveTab("register")}
                  className="w-full mb-6 py-2.5 px-4 rounded-xl font-medium text-sm transition-all bg-slate-800/30 text-green-400 hover:bg-green-900/30 border border-green-800/30 hover:border-green-600/50"
                >
                  <div className="flex items-center justify-center gap-2">
                    <UserPlus className="h-4 w-4" />
                    {t('login.dontHaveAccount')} - {t('login.register')}
                  </div>
                </button>
              )}
            </>
          )}

          {/* Login Forms */}
          {activeTab === "player" ? (
            <SimplePlayerLogin 
              isLoading={isLoading} 
              setIsLoading={setIsLoading} 
              setEvaluator={setEvaluator} 
            />
          ) : activeTab === "evaluator" ? (
            <EvaluatorLoginTab 
              isLoading={isLoading} 
              setIsLoading={setIsLoading} 
              setEvaluator={setEvaluator} 
            />
          ) : (
            <PlayerRegister onBack={() => setActiveTab("player")} />
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-slate-600 text-xs mt-6">
          Desenvolvido por Jhonatas Valim
        </p>
      </div>
    </div>
  );
}
